import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Text } from '@chakra-ui/react';
import {
  getSuperStokezes,
  createSuperStokez,
  updateSuperStokez,
  toggleLockSuperStokez,
  deleteSuperStokez
} from '../actions/superStokezActions';
import { getGames } from '../actions/gameActions';
import { getAdmins } from '../actions/adminActions';
import { fetchCompanies } from '../actions/companyActions';
import CascadingDropdown from '../components/CascadingDropdown';
import GameSelection from './GameSelection';
import GenericTable from '../components/GenericTable';
import BalanceTransferModal from '../components/BalanceTransferModal';
import TableSearchBar from '../components/TableSearchBar';
import { Plus, ChevronRight, DollarSign, Lock, Unlock, Loader2, X, Edit } from 'lucide-react';
import { throwUnlessApiSuccess } from '../utils/apiResponse';
import { applyRecipientBalancePatch } from '../utils/applyRecipientBalancePatch';
import styles from '../styles/ManagementTable.module.css';
import { usePrivilege } from '../hooks/usePrivilege';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';
import { useAppToast } from '../hooks/useAppToast';
import { findById, filterGamesByAssignedIds, idStr } from '../utils/entityIds';
import { getLoggedInUserRole } from '../utils/auth';

const EMPTY_SUPER_STOKEZ = {
  username: '',
  password: '',
  admin_id: '',
  company_id: '',
  gameIds: [],
  isseparateresultbygame: false,
  separateResultGameIds: [],
  commission_perc: 5,
  target_perc: 15,
  adminTargetNTP: '',
};

const SuperStokezManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug } = useParams();
  const location = useLocation();
  const { can } = usePrivilege();
  const { filterColumns, canShowField } = useFieldPrivilege();
  const { showSuccess, showWarning, showApiError } = useAppToast();
  const { superStokezes, loading, error } = useSelector(state => state.superStokez);

  const games = useSelector(state => state.game.games);
  const admins = useSelector(state => state.admin.admins);
  const companies = useSelector(state => state.company.companies);

  const filterAdminId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const adminParam = params.get('admin') || null;
    if (!adminParam) return null;
    const found = admins.find((a) => a.username === adminParam);
    return found ? found._id : null;
  }, [location.search, admins]);

  const [searchQuery, setSearchQuery] = useState('');

  const filteredByAdmin = useMemo(() => {
    if (!filterAdminId) return superStokezes;
    return superStokezes.filter((s) => s.admin_id === filterAdminId);
  }, [superStokezes, filterAdminId]);

  const filteredSuperStokezes = useMemo(() => {
    if (!searchQuery.trim()) return filteredByAdmin;
    const q = searchQuery.trim().toLowerCase();
    return filteredByAdmin.filter((s) => {
      const username = (s.username || '').toLowerCase();
      const admin = (s.admin_name || '').toLowerCase();
      const company = (s.company_name || '').toLowerCase();
      const gameCodes = (s.game_codes || []).join(' ').toLowerCase();
      return [username, admin, company, gameCodes].some((str) => str.includes(q));
    });
  }, [filteredByAdmin, searchQuery]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [newSuperStokez, setNewSuperStokez] = useState({ ...EMPTY_SUPER_STOKEZ });
  const [availableGames, setAvailableGames] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const isSuperAdmin = getLoggedInUserRole() === 'super_admin';
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [transferDetails, setTransferDetails] = useState({});

  const handleOpenTransferModal = (toRole, toId, recipient = {}) => {
    setTransferDetails({
      toRole,
      toId,
      recipientBalance: recipient.balance,
      recipientBalanceCheck: recipient.balance_check,
    });
    setIsTransferOpen(true);
  };

  const handleBalanceTransfer = async ({ fromRole, fromId, toRole, toId, amount }) => {
    const response = await axios.post(`${process.env.REACT_APP_API_URL_ADMIN}`, {
      apiName: 'user',
      command: 'transferBalance',
      fromRole,
      fromId,
      toRole,
      toId,
      amount,
    });
    return throwUnlessApiSuccess(response);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(getSuperStokezes()),
          dispatch(getGames()),
          dispatch(getAdmins()),
          dispatch(fetchCompanies())
        ]);
        console.log('Data fetched successfully');
      } catch (err) {
        console.error('Error fetching data:', err);
        showApiError(err, 'Error fetching data');
      }
    };
    fetchData();
  }, [dispatch, showApiError]);

  useEffect(() => {
    console.log('Current superStokezes:', superStokezes);
    console.log('Current games:', games);
    console.log('Current admins:', admins);
    console.log('Current companies:', companies);
  }, [superStokezes, games, admins, companies]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewSuperStokez(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCascadingDropdownChange = useCallback((selections) => {
    setNewSuperStokez(prev => ({
      ...prev,
      ...selections,
      gameIds: [] // Reset gameIds when selections change
    }));
  }, []);

  // Keep games in sync when admin_id is set (incl. session auto-fill) and lists load
  useEffect(() => {
    if (!newSuperStokez.admin_id) {
      setAvailableGames([]);
      return;
    }
    const selectedAdmin = findById(admins, newSuperStokez.admin_id);
    const filteredGames = filterGamesByAssignedIds(games, selectedAdmin?.gameIds);
    if (filteredGames.length) {
      setAvailableGames(filteredGames);
    } else if (games.length > 0 && newSuperStokez.admin_id) {
      // Empty/missing gameIds or id-type mismatch against Redis catalog — still show games
      setAvailableGames(games);
    } else {
      setAvailableGames([]);
    }
  }, [newSuperStokez.admin_id, admins, games]);



  const handleGameSelection = useCallback((gameId) => {
    const id = idStr(gameId);
    setNewSuperStokez(prev => {
      const prevGameIds = (prev.gameIds || []).map(idStr);
      return {
        ...prev,
        gameIds: prevGameIds.includes(id)
          ? prevGameIds.filter((gid) => gid !== id)
          : [...prevGameIds, id]
      };
    });
  }, []);

  const handleSeparateResultGameSelection = useCallback((gameId) => {
    const id = idStr(gameId);
    setNewSuperStokez(prev => {
      const prevGameIds = (prev.separateResultGameIds || []).map(idStr);
      return {
        ...prev,
        separateResultGameIds: prevGameIds.includes(id)
          ? prevGameIds.filter((gid) => gid !== id)
          : [...prevGameIds, id]
      };
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!newSuperStokez.admin_id || !newSuperStokez.company_id) {
        showWarning('Please select both Admin and Company');
        return;
      }
      if (isEditing) {
        await dispatch(updateSuperStokez(newSuperStokez._id, newSuperStokez));
        showSuccess('Super Stokez updated.');
      } else {
        await dispatch(createSuperStokez(newSuperStokez));
        showSuccess('Super Stokez created.');
      }
      setIsModalOpen(false);
      setNewSuperStokez({ ...EMPTY_SUPER_STOKEZ });
      setIsEditing(false);
      dispatch(getSuperStokezes());
    } catch (error) {
      console.error('Failed to create/update super stokez:', error);
      showApiError(error, `Failed to ${isEditing ? 'update' : 'create'} Super Stokez. Please try again.`);
    }
  };

  const handleEdit = (superStokez) => {
    console.log('Editing Super Stokez:', superStokez);
    setNewSuperStokez({
      ...superStokez,
      password: '', // Clear password for security reasons
      isseparateresultbygame: superStokez.isseparateresultbygame ?? false,
      separateResultGameIds: superStokez.separateResultGameIds || [],
      commission_perc: superStokez.commission_perc ?? 5,
      target_perc: superStokez.target_perc ?? 15,
      adminTargetNTP: superStokez.adminTargetNTP ?? '',
    });
    setIsEditing(true);
    const selectedAdmin = findById(admins, superStokez.admin_id);
    const filteredGames = filterGamesByAssignedIds(games, selectedAdmin?.gameIds);
    setAvailableGames(filteredGames.length ? filteredGames : games);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNewSuperStokez({ ...EMPTY_SUPER_STOKEZ });
    setIsEditing(false);
  };

  const handleToggleLock = async (superStokezId, currentLockStatus) => {
    try {
      await dispatch(toggleLockSuperStokez(superStokezId));
      dispatch(getSuperStokezes()); // Refresh the data
      showSuccess(`Super Stokez ${currentLockStatus ? 'unlocked' : 'locked'}.`);
    } catch (error) {
      console.error('Failed to toggle lock status:', error);
      showApiError(error, 'Failed to update lock status. Please try again.');
    }
  };

  const handleDelete = async (superStokezId) => {
    if (window.confirm('Are you sure you want to delete this Super Stokez?')) {
      try {
        await dispatch(deleteSuperStokez(superStokezId));
        dispatch(getSuperStokezes()); // Refresh the data
        showSuccess('Super Stokez deleted.');
      } catch (error) {
        console.error('Failed to delete Super Stokez:', error);
        showApiError(error, 'Failed to delete Super Stokez. Please try again.');
      }
    }
  };

 

  const columns = useMemo(() => [
    {
      Header: 'Username',
      accessor: 'username',
      defaultWidth: 120,
    },
    {
      Header: 'Password',
      accessor: 'password',
      defaultWidth: 100,
    },
    {
      Header: 'Admin',
      accessor: 'admin_name',
      defaultWidth: 120,
    },
    {
      Header: 'Company',
      accessor: 'company_name',
      defaultWidth: 150,
    },
    {
      Header: 'Balance',
      accessor: 'balance',
      defaultWidth: 100,
      Cell: ({ row }) => <Text>{row.original.balance}</Text>,
    },
    {
      Header: 'Comm %',
      accessor: 'commission_perc',
      defaultWidth: 80,
      Cell: ({ value }) => value ?? '—',
    },
    {
      Header: 'Target %',
      accessor: 'target_perc',
      defaultWidth: 80,
      Cell: ({ value }) => value ?? '—',
    },
    {
      Header: 'Admin Target NTP',
      accessor: 'adminTargetNTP',
      defaultWidth: 120,
      Cell: ({ value }) => (value != null && value !== '' ? value : '—'),
    },
    {
      Header: 'Game Codes',
      accessor: 'game_codes',
      defaultWidth: 150,
      Cell: ({ value }) => value ? value.join(', ') : '',
    },
    {
      Header: 'Status',
      accessor: 'isLocked',
      defaultWidth: 80,
      Cell: ({ row, value }) => (
        can('superStokez.lock') ? (
        <Switch
          isChecked={!value}
          onChange={() => handleToggleLock(row.original._id, value)}
          colorScheme={value ? "red" : "green"}
        />
        ) : (
          <Text fontSize="sm">{value ? 'Locked' : 'Active'}</Text>
        )
      )
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      defaultWidth: 220,
      Cell: ({ row }) => (
        <div className={styles.actionButtons}>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => {
              const base = slug ? `/${slug}/stokez` : '/stokez';
              const params = new URLSearchParams(location.search);
              params.set('super_stokez', row.original.username || row.original._id);
              navigate(`${base}?${params.toString()}`);
            }}
            title="Go to Stokez"
            aria-label="Go to Stokez"
          >
            <ChevronRight size={18} />
          </button>
          {can('superStokez.edit') && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => handleEdit(row.original)}
            title="Edit Super Stokez"
            aria-label="Edit Super Stokez"
          >
            <Edit size={18} />
          </button>
          )}
          {can('superStokez.lock') && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => handleToggleLock(row.original._id, row.original.isLocked)}
            title={row.original.isLocked ? 'Unlock' : 'Lock'}
            aria-label={row.original.isLocked ? 'Unlock' : 'Lock'}
          >
            {row.original.isLocked ? <Lock size={18} /> : <Unlock size={18} />}
          </button>
          )}
          {can('superStokez.transfer') && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => handleOpenTransferModal('superStokez', row.original.username, row.original)}
            title="Transfer Balance"
            aria-label="Transfer Balance"
          >
            <DollarSign size={18} />
          </button>
          )}
        </div>
      ),
    },
  ], [handleToggleLock, handleEdit, navigate, slug, location.search, can]);

  const visibleColumns = useMemo(
    () => filterColumns('superStokez', columns),
    [columns, filterColumns]
  );

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 className={styles.spinner} size={48} />
      </div>
    );
  }
  if (error && superStokezes.length === 0) {
    return (
      <div className={styles.errorAlert}>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className={styles.managementPage}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon} aria-hidden><Plus size={24} /></span>
          <h1 className={styles.title}>Super Stokez Management</h1>
        </div>
        {can('superStokez.create') && (
        <button
          type="button"
          className={styles.addButton}
          onClick={() => {
            setIsEditing(false);
            setNewSuperStokez({ ...EMPTY_SUPER_STOKEZ });
            setIsModalOpen(true);
          }}
        >
          <Plus size={20} />
          <span>Add Super Stokez</span>
        </button>
        )}
      </div>

      <div className={styles.searchBarRow}>
        <TableSearchBar
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by username, admin, company..."
        />
      </div>

      <div className={styles.tableContainer}>
        <GenericTable columns={visibleColumns} data={filteredSuperStokezes} showColumnFilters={false} />
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {isEditing ? 'Edit Super Stokez' : 'Add New Super Stokez'}
              </h2>
              <button type="button" className={styles.closeButton} onClick={handleCloseModal} aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <form className={styles.modalForm} onSubmit={handleSubmit}>
              <div className={styles.modalBody}>
                {canShowField('superStokez', 'username', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Username <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={newSuperStokez.username}
                    onChange={handleInputChange}
                    className={styles.input}
                    required
                  />
                </div>
                )}
                {canShowField('superStokez', 'password', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Password {isEditing && <span className={styles.helpText}>(Leave blank to keep current password)</span>}
                    {!isEditing && <span className={styles.required}>*</span>}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={newSuperStokez.password}
                    onChange={handleInputChange}
                    className={styles.input}
                    required={!isEditing}
                  />
                </div>
                )}
                {(canShowField('superStokez', 'admin_id', 'form') ||
                  canShowField('superStokez', 'company_id', 'form')) && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Admin / Company <span className={styles.required}>*</span>
                  </label>
                  <CascadingDropdown
                    onSelectionChange={handleCascadingDropdownChange}
                    initialValues={{
                      company_id: newSuperStokez.company_id,
                      admin_id: newSuperStokez.admin_id
                    }}
                    roleBeingCreated="super_stokez"
                  />
                </div>
                )}
                {canShowField('superStokez', 'gameIds', 'form') && (
                    <div className={styles.formGroup}>
                      <label className={styles.label}>Games</label>
                      {availableGames.length > 0 ? (
                        <GameSelection
                          games={availableGames}
                          selectedGames={newSuperStokez.gameIds}
                          onGameSelection={handleGameSelection}
                          isDisabled={!newSuperStokez.admin_id}
                        />
                      ) : (
                        <p className={styles.helpText}>No games available for the selected admin</p>
                      )}
                    </div>
                    )}
                    {canShowField('superStokez', 'isseparateresultbygame', 'form') && (
                    <div className={styles.formGroup}>
                      <label className={styles.switchLabel}>
                        <input
                          type="checkbox"
                          name="isseparateresultbygame"
                          checked={newSuperStokez.isseparateresultbygame}
                          onChange={handleInputChange}
                          className={styles.switch}
                        />
                        <span className={styles.switchText}>Separate Result By Game</span>
                      </label>
                    </div>
                    )}
                    {newSuperStokez.isseparateresultbygame &&
                      canShowField('superStokez', 'separateResultGameIds', 'form') && (
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Games for Separate Results</label>
                        <p className={styles.helpText} style={{ marginBottom: '0.5rem' }}>
                          Select games for which results should be separated:
                        </p>
                        <GameSelection
                          games={availableGames.filter(g => newSuperStokez.gameIds.includes(g._id))}
                          selectedGames={newSuperStokez.separateResultGameIds}
                          onGameSelection={handleSeparateResultGameSelection}
                          isDisabled={!newSuperStokez.admin_id}
                        />
                      </div>
                    )}
                {(canShowField('superStokez', 'commission_perc', 'form') ||
                  canShowField('superStokez', 'target_perc', 'form') ||
                  (isSuperAdmin && canShowField('superStokez', 'adminTargetNTP', 'form'))) && (
                  <>
                    <h3 className={styles.formSectionTitle}>Percentages</h3>
                    <div className={styles.formGrid}>
                      {canShowField('superStokez', 'commission_perc', 'form') && (
                        <div className={styles.formGroup}>
                          <label className={styles.label}>Commission %</label>
                          <input
                            type="number"
                            name="commission_perc"
                            step="any"
                            value={newSuperStokez.commission_perc}
                            onChange={handleInputChange}
                            className={styles.input}
                          />
                        </div>
                      )}
                      {canShowField('superStokez', 'target_perc', 'form') && (
                        <div className={styles.formGroup}>
                          <label className={styles.label}>Target %</label>
                          <input
                            type="number"
                            name="target_perc"
                            step="any"
                            min={isSuperAdmin ? undefined : 0}
                            max={isSuperAdmin ? undefined : 100}
                            value={newSuperStokez.target_perc}
                            onChange={handleInputChange}
                            className={styles.input}
                          />
                        </div>
                      )}
                      {isSuperAdmin && canShowField('superStokez', 'adminTargetNTP', 'form') && (
                        <div className={styles.formGroup}>
                          <label className={styles.label}>Admin Target NTP</label>
                          <input
                            type="number"
                            name="adminTargetNTP"
                            step="any"
                            value={newSuperStokez.adminTargetNTP}
                            onChange={handleInputChange}
                            className={styles.input}
                            placeholder="Leave empty for none"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className={styles.modalFooter}>
                <button type="submit" className={styles.submitButton}>
                  {isEditing ? 'Update Super Stokez' : 'Add Super Stokez'}
                </button>
                <button type="button" className={styles.cancelButton} onClick={handleCloseModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <BalanceTransferModal
        isOpen={isTransferOpen}
        onClose={() => setIsTransferOpen(false)}
        toRole={transferDetails.toRole}
        toId={transferDetails.toId}
        recipientBalance={transferDetails.recipientBalance}
        recipientBalanceCheck={transferDetails.recipientBalanceCheck}
        onTransfer={handleBalanceTransfer}
        onTransferSuccess={(r) => applyRecipientBalancePatch(dispatch, r)}
      />
    </div>
  );
};

export default SuperStokezManagement;